import { useEffect, useRef, type CSSProperties, type ReactNode } from "react";
import {
  headerSpec,
  modalSpec,
  segmentedControlSpec,
  shellSpec,
  sidebarSpec,
  tabsSpec,
  type TabsSize,
  type TabsVariant,
} from "../specs/navigation";
import { mergeVars, typographyClass } from "../specs/types";

/* ------------------------------------------------------------------- Tabs */

export interface TabItem<T extends string> {
  value: T;
  label: ReactNode;
  /** Текстовая подпись, когда `label` — иконка: уходит в aria-label и тултип. */
  title?: string;
}

export function Tabs<T extends string>({
  items,
  value,
  onChange,
  size = tabsSpec.defaults.size,
  variant = tabsSpec.defaults.variant,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: TabsSize;
  variant?: TabsVariant;
}) {
  const sizeSpec = tabsSpec.sizes[size];
  return (
    <div
      className="ui-tabs"
      role="tablist"
      style={mergeVars(tabsSpec.base, sizeSpec.vars, tabsSpec.variants[variant]) as CSSProperties}
    >
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={item.value === value}
          data-active={item.value === value}
          className={`ui-tab ${typographyClass(sizeSpec.typography)}`}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/* -------------------------------------------------------- SegmentedControl */

export function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
  iconOnly = false,
}: {
  items: TabItem<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Сегменты только с иконками — квадратные, подпись берётся из `title`. */
  iconOnly?: boolean;
}) {
  return (
    <div className="ui-segmented" style={segmentedControlSpec.base as CSSProperties}>
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          data-active={item.value === value}
          data-icon-only={iconOnly || undefined}
          aria-label={item.title}
          aria-pressed={item.value === value}
          title={item.title}
          className={`ui-segment ${typographyClass(segmentedControlSpec.typography)}`}
          onClick={() => onChange(item.value)}
        >
          {item.label}
        </button>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------- Header */

export function Header({
  logo,
  caption,
  leading,
  nav,
  actions,
}: {
  /** Логотип продукта — он же название: отдельной текстовой подписи рядом нет. */
  logo: ReactNode;
  /** Что это за портал и его версия. Отделяется от логотипа вертикальной чертой. */
  caption?: ReactNode;
  /** Место слева от бренда: на узких экранах здесь живёт кнопка бокового меню. */
  leading?: ReactNode;
  /** Верхнеуровневые разделы портала. Живут между брендом и действиями. */
  nav?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="ui-header" style={headerSpec.base as CSSProperties}>
      {leading}
      <div className="ui-header__brand">
        {logo}
        {caption && (
          <>
            <span className="ui-header__divider" aria-hidden="true" />
            <span className={`ui-header__caption ${typographyClass(headerSpec.captionTypography)}`}>{caption}</span>
          </>
        )}
      </div>
      {nav && <nav className="ui-header__nav">{nav}</nav>}
      {actions && <div className="ui-header__actions">{actions}</div>}
    </header>
  );
}

/** Вкладка верхнего уровня в шапке. */
export function HeaderTab({
  children,
  active,
  as: Component = "button",
  ...rest
}: {
  children: ReactNode;
  active?: boolean;
  as?: React.ElementType;
} & Record<string, unknown>) {
  return (
    <Component
      {...rest}
      type={Component === "button" ? "button" : undefined}
      data-active={active}
      className={`ui-header__tab ${typographyClass(headerSpec.tabTypography)}`}
    >
      {children}
    </Component>
  );
}

/* ---------------------------------------------------------------- Sidebar */

/**
 * Боковая навигация.
 *
 * На широком экране — постоянная колонка слева. Ниже брейкпоинта она
 * превращается в выезжающую шторку: за неё отвечают `open`/`onClose`, а
 * прячет и показывает её CSS. Из потока и из порядка табуляции закрытая
 * шторка выпадает через `visibility` — отдельного `aria-hidden` не нужно,
 * иначе на широком экране пришлось бы гасить видимую панель.
 */
export function Sidebar({
  children,
  open = false,
  onClose,
  label,
}: {
  children: ReactNode;
  open?: boolean;
  onClose?: () => void;
  /** Имя шторки для скринридера. Нужно, пока она открыта: тогда это диалог. */
  label?: string;
}) {
  const asideRef = useRef<HTMLElement>(null);

  // Открытая шторка перекрывает страницу, поэтому и фокус должен жить внутри
  // неё: иначе Tab уводит на кнопки шапки и содержимого за подложкой, где
  // мышью ничего не нажать. На широком экране `open` всегда false, и панель
  // остаётся обычной колонкой без ловушки и без роли диалога.
  useEffect(() => {
    const aside = asideRef.current;
    if (!open || !aside) return;

    const focusable = () =>
      Array.from(
        aside.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
        // Скрытые пункты (свёрнутый список моделей) в обход не берём.
      ).filter((element) => element.offsetParent !== null);

    const returnTo = document.activeElement as HTMLElement | null;
    (focusable()[0] ?? aside).focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = focusable();
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!aside.contains(active)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Закрылись — возвращаем фокус туда, откуда шторку открыли: это кнопка
      // меню, и человек продолжает с того же места.
      returnTo?.focus();
    };
  }, [open]);

  return (
    <>
      <div className="ui-sidebar__scrim" data-open={open || undefined} onClick={onClose} aria-hidden="true" />
      <aside
        ref={asideRef}
        className="ui-sidebar"
        data-open={open || undefined}
        role={open ? "dialog" : undefined}
        aria-modal={open || undefined}
        aria-label={open ? label : undefined}
        tabIndex={open ? -1 : undefined}
        style={sidebarSpec.base as CSSProperties}
      >
        {children}
      </aside>
    </>
  );
}

export function SidebarSection({ label, children }: { label?: ReactNode; children: ReactNode }) {
  return (
    <div className="ui-sidebar__section">
      {label && <span className={`ui-sidebar__label ${typographyClass(sidebarSpec.labelTypography)}`}>{label}</span>}
      {children}
    </div>
  );
}

export function NavItem({
  children,
  active,
  onClick,
  leading,
  trailing,
  as: Component = "button",
  ...rest
}: {
  children: ReactNode;
  active?: boolean;
  onClick?: () => void;
  leading?: ReactNode;
  /** Прижимается к правому краю пункта — например, шеврон раскрытия. */
  trailing?: ReactNode;
  as?: React.ElementType;
} & Record<string, unknown>) {
  return (
    <Component
      {...rest}
      type={Component === "button" ? "button" : undefined}
      data-active={active}
      onClick={onClick}
      className={`ui-nav-item ${typographyClass(sidebarSpec.itemTypography)}`}
    >
      {leading}
      <span className="ui-nav-item__text">{children}</span>
      {trailing}
    </Component>
  );
}

/* ------------------------------------------------------------------ Modal */

export function Modal({
  open,
  title,
  onClose,
  children,
  actions,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  actions?: ReactNode;
}) {
  if (!open) return null;
  return (
    <div
      className="ui-modal-overlay"
      role="dialog"
      aria-modal="true"
      style={modalSpec.base as CSSProperties}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="ui-modal">
        <h3 className={typographyClass(modalSpec.titleTypography)}>{title}</h3>
        {children}
        {actions && <div className="ui-modal__actions">{actions}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Shell */

export function Shell({ sidebar, children }: { sidebar?: ReactNode; children: ReactNode }) {
  return (
    <div className="ui-shell" style={shellSpec.base as CSSProperties}>
      {sidebar}
      <main className="ui-shell__main">
        <div className="ui-shell__inner">{children}</div>
      </main>
    </div>
  );
}
